import moment from 'moment';
import { ExportCoreUtils } from '../components/ProofOfDeliveryLayout/ExportButton/ExportCore';
import campaignValues from '../utils/campaignValues';
import dateFunctions from '../utils/dateFunctions.js';
import NumberRounding from '../utils/numberRounding.js';
import QueryStringService from './queryStringService.js';
import { COUNTRY_CODES, PRODUCT_TYPES } from '../constants/enums.js';

class ExportService {
    static exportToExcel(filename, sheetName, data) {
        if (!data || !Array.isArray(data) || data.length === 0) {
            return Promise.reject(new Error('No data to export'));
        }

        try {
            // Add timestamp to filename
            const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
            const fullFilename = `${filename}_${timestamp}.xlsx`;

            // Use ExportCoreUtils to create the Excel file
            return ExportCoreUtils.handleExcelExport(data, fullFilename, sheetName)
                .then(() => {
                    console.log(`Excel export successful: ${fullFilename}`);
                    return { success: true, filename: fullFilename };
                });
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            return Promise.reject(error);
        }
    }

    post(url, body, successCallback, errorCallback) {
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.arrayBuffer();
            })
            .then(data => successCallback(data))
            .catch(error => errorCallback(error));
    }

    get(url, successCallback, errorCallback) {
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => successCallback(data))
            .catch(error => errorCallback(error));
    }

    get_with_arraybuffer(url, successCallback, errorCallback) {
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.arrayBuffer();
            })
            .then(data => successCallback(data))
            .catch(error => errorCallback(error));
    }

    head(url, successCallback, errorCallback) {
        fetch(url, {
            method: 'HEAD',
            headers: {
                'Content-Type': 'application/json'
            }
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                successCallback(null, 'success', response);
                return response;
            })
            .catch(error => errorCallback(error));
    }

    getSpotplan(spotlength, SoV, successCallback, errorCallback) {
        const body = {
            spotlength: spotlength,
            SoV: SoV
        };
        this.post("/api/exportspotplan", body, successCallback, errorCallback);
    }

    getOiData(orderlineId, successCallback, errorCallback) {
        this.get("/api/getOIData?orderlineid=" + orderlineId, successCallback, errorCallback);
    }

    getIDSExport(orderlineid, successCallback, errorCallback) {
        this.get_with_arraybuffer("/api/exportidsresult?orderlineid=" + orderlineid, successCallback, errorCallback);
    }

    checkIfIDSExportExists(orderlineid, successCallback, errorCallback) {
        this.head("/api/exportidsresult?orderlineid=" + orderlineid, successCallback, errorCallback);
    }

    getIDSResultValues(orderlineid, successCallback, errorCallback) {
        this.get("/api/idsresultvalues?orderlineid=" + orderlineid, successCallback, errorCallback);
    }

    static async downloadPanelPictures(zipFileName, pictureInfoList) {
        const response = await fetch('/api/picture/bundle', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ zipFileName: zipFileName, pictures: pictureInfoList }),
        });

        if (!response.ok) {
            throw new Error('Server failed to download panel pictures');
        }

        // Get the blob from response
        return await response.blob();
    }

    static async downloadEffectMeasurePdf(storedAs) {
        const response = await fetch('/api/effectmeasure', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Server failed to download panel pictures');
        }

        // Get the blob from response
        return await response.blob();
    }

    static async downloadCampaignMaterial(zipFileName, materialInfoList) {
        const response = await fetch('/api/AdCopy/bundle', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ zipFileName: zipFileName, material: materialInfoList }),
        });

        if (!response.ok) {
            throw new Error('Server failed to download campaign material');
        }

        // Get the blob from response
        return await response.blob();
    }

    static exportCampaignEffectData(filename, campaignData) {
        return Promise.reject(new Error('Not implemented'));
    }

    static createAddressListExportHandler(options) {
        const {
            panels = [],
            orderData = {},
            selectedOrderlineMetaData = [],
            campaignType = PRODUCT_TYPES.CLASSIC,
            campaignName = '',
            campaignPromiseType = '',
            debugPosting = false,
            exportPanelSOVValue = false,
            exportPanelVACValue = false
        } = options;

        const isNonMOMSFollowUp = campaignValues.isNonMOMSFollowUpCampaign(campaignPromiseType);

        return async () => {
            try {
                if (!panels || panels.length === 0) {
                    throw new Error('No panels available for export');
                }

                const queryStringService = new QueryStringService();
                const countryCode = queryStringService.getCurrentCountry();
                const selectedMetadata = selectedOrderlineMetaData?.[0];

                let formattedPanels;
                if (campaignType === PRODUCT_TYPES.CLASSIC) {
                    const mergedPanels = panels.flatMap(item => item?.panels || []);
                    formattedPanels = this._formatPanelsForExport(
                        mergedPanels,
                        countryCode,
                        orderData,
                        selectedMetadata,
                        isNonMOMSFollowUp
                    );
                } else {
                    // For play campaigns, just format
                    formattedPanels = this._formatPlayPanelsForExport(
                        panels,
                        countryCode,
                        orderData,
                        selectedMetadata,
                        exportPanelSOVValue,
                        exportPanelVACValue
                    );
                }

                // Generate timestamp for the export filename
                const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
                const exportFileName = `AddressList_${campaignName || 'Campaign'}_${timestamp}`;

                // Use ExportCoreUtils to handle the Excel export
                return ExportCoreUtils.handleExcelExport(
                    formattedPanels,
                    `${exportFileName}.xlsx`,
                    'Address List'
                ).then(() => {
                    return { success: true, filename: exportFileName };
                });

            } catch (error) {
                console.error('Error in address list export handler:', error);
                throw error;
            }
        };
    }

    // Remaining methods (private helpers) stay the same...
    static _removeDuplicatePanels(countryCode, panels, debugPosting) {
        const uniquePanelsMap = new Map();

        panels.forEach(panel => {
            if (!panel.panels || panel.panels.length === 0) {
                // Handle panel without sub-panels (for Map view panels)
                const key = [
                    panel.panelId,
                    panel.address,
                    panel.postalCode,
                    panel.city,
                    panel.region,
                    panel.status,
                    panel.latitude,
                    panel.longitude
                ].join('|');

                if (!uniquePanelsMap.has(key)) {
                    uniquePanelsMap.set(key, {
                        ...panel,
                        debugInfos: debugPosting ? [panel.debugInfo] : []
                    });
                }
                return;
            }

            panel.panels.forEach(subpanel => {
                // Create a unique key from all the specified fields
                const key = [
                    panel.panelId,
                    panel.address,
                    panel.postalCode,
                    panel.city,
                    panel.alternateAddress,
                    panel.county,
                    panel.region,
                    subpanel.status,
                    dateFunctions.getCountrySpecificDateTimeFormatFromDate(
                        countryCode,
                        moment(subpanel.timeStamp)
                    ),
                    subpanel.design,
                    subpanel.slotId,
                    dateFunctions.getCountrySpecificDateFormatFromDate(countryCode, panel.plannedDate),
                    panel.latitude,
                    panel.longitude
                ].join('|');

                if (!uniquePanelsMap.has(key)) {
                    uniquePanelsMap.set(key, {
                        ...subpanel,
                        panelId: panel.panelId,
                        address: panel.address,
                        postalCode: panel.postalCode,
                        city: panel.city,
                        region: panel.region,
                        latitude: panel.latitude,
                        longitude: panel.longitude,
                        debugInfos: debugPosting ? [panel.debugInfo] : []
                    });
                } else if (debugPosting) {
                    const existingPanel = uniquePanelsMap.get(key);
                    existingPanel.debugInfos.push(panel.debugInfo);
                }
            });
        });

        return Array.from(uniquePanelsMap.values()).map(panel => ({
            ...panel,
            debugInfo: panel.debugInfos?.join(' | ') || panel.debugInfo
        }));
    }

    static _formatPanelsForExport(panels, countryCode, orderData, selectedMetadata, isNonMOMSFollowUp = false) {
        return panels.map(panel => {
            const baseData = {
                Panel: `${panel.panelId || ''}`,
                Address: `${panel.address || ''}`,
                PostalCode: `${panel.postalCode || ''}`,
                City: `${panel.city || ''}`,
                Region: `${panel.region || ''}`,
                Status: `${panel.status || ''}`,
                ...(isNonMOMSFollowUp ? {} : {
                    Timestamp: `${panel.timeStamp ? dateFunctions.getCountrySpecificDateTimeFormatFromDate(
                        countryCode, moment(panel.timeStamp)
                    ) : ''}`
                }),
                Design: `${panel.design || ''}`,
                SlotId: `${panel.slotId || ''}`,
                PlannedPostingDate: `${panel.plannedDate ?
                    dateFunctions.getCountrySpecificDateFormatFromDate(countryCode, panel.plannedDate)
                    : ''}`,
                Latitude: `${panel.latitude || ''}`,
                Longitude: `${panel.longitude || ''}`,
                Info: `${panel.debugInfo || ''}`
            };

            // Add country-specific fields
            switch (countryCode) {
                case COUNTRY_CODES.SWEDEN:
                    return {
                        ...baseData,
                        FaceId: `${panel.externalKey || ''}`
                    };
                case COUNTRY_CODES.NORWAY:
                    return {
                        Advertiser: `${orderData.customer || ''}`,
                        Campaign: `${selectedMetadata ? campaignValues.caluclateCampaignName(
                            selectedMetadata.alternativeName,
                            orderData.customer,
                            selectedMetadata.periodStart,
                            selectedMetadata.periodEnd
                        ) : ''}`,
                        ProductType: 'Classic',
                        Period: `${selectedMetadata ?
                            `Week ${dateFunctions.calculateCampaignWeeks(
                                selectedMetadata.periodStart,
                                selectedMetadata.periodEnd
                            )}` : 'Week '}`,
                        ...baseData
                    };
                case COUNTRY_CODES.DENMARK:
                default:
                    return baseData;
            }
        });
    }

    static _formatPlayPanelsForExport(panels, countryCode, orderData, selectedMetadata, exportPanelSOVValue, exportPanelVACValue) {
        return panels.map(panel => {
            const baseData = {
                Panel: `${panel.panelId || ''}`,
                Address: `${panel.address || ''}`,
                PostalCode: `${panel.postalCode || ''}`,
                City: `${panel.city || ''}`,
                Region: `${panel.region || ''}`,
                Status: `${panel.status || ''}`,
                Latitude: `${panel.latitude || ''}`,
                Longitude: `${panel.longitude || ''}`,
                Environment: `${panel.environment || ''}`
            };

            // Add country-specific fields
            switch (countryCode) {
                case COUNTRY_CODES.SWEDEN:
                    return {
                        ...baseData,
                        SOV: exportPanelSOVValue ? `${NumberRounding.round(
                            (!isNaN(panel.soV) && typeof panel.soV !== "number" ? 0.0 : +panel.soV) * 100, 2
                        )}` : '',
                        FaceId: `${panel.externalKey || ''}`
                    };
                case COUNTRY_CODES.NORWAY:
                    return {
                        Advertiser: `${orderData.customer || ''}`,
                        Campaign: `${selectedMetadata ? campaignValues.caluclateCampaignName(
                            selectedMetadata.alternativeName,
                            orderData.customer,
                            selectedMetadata.periodStart,
                            selectedMetadata.periodEnd
                        ) : ''}`,
                        ProductType: 'Digital',
                        Period: `${selectedMetadata ?
                            `Week ${dateFunctions.calculateCampaignWeeks(
                                selectedMetadata.periodStart,
                                selectedMetadata.periodEnd
                            )}` : 'Week '}`,
                        ...baseData,
                        Info: `${panel.debugInfo || ''}`
                    };
                case COUNTRY_CODES.DENMARK:
                default:
                    if (exportPanelVACValue) {
                        return {
                            ...baseData,
                            VAC: `${
                                !isNaN(panel.delivered) && typeof panel.delivered !== "number" ? 0 : +panel.delivered
                            }`
                        };
                    } else 
                        return baseData;
            }
        });
    }
}

export default ExportService;